import { Tractor } from "@deathstar/types/northstar";
import { PrimaryButton, useSnackbar } from "@deathstar/ui";
import { CircularProgress } from "@material-ui/core";
import { useEffect, useState } from "react";
import api from "../../api/api";
import { EquipmentTypeString } from "../../api/queries/equipment";
import { UnauthenticatedError, UnauthorizedError } from "../../api/util/exceptions";
import { Dialog } from "../../components/dialog/Dialog";
import { usePendingPolicyChanges } from "../pending-policy-changes/PendingPolicyChanges";
import { SupportingDocumentsInput, uploadSupportingDocuments } from "./SupportingDocumentsInput";

export default function RemoveUnitDialog({
    unitType,
    accountId,
    unitId,
    coverages,
    open,
    onClose,
    defaultDate,
    deferPolicyChanges,
    pendingChangeDetail,
}: {
    unitType: EquipmentTypeString;
    accountId: string;
    unitId: number;
    coverages: Tractor["coverages"];
    open: boolean;
    onClose(): void;
    defaultDate?: string;
    deferPolicyChanges?: boolean;
    pendingChangeDetail?: string;
}) {
    const [date, setDate] = useState<string | null>(null);
    const [supportingDocuments, setSupportingDocuments] = useState<File[]>([]);
    const pendingPolicyChanges = usePendingPolicyChanges();
    const remove = api.equipment.useUpdate(unitType, accountId, {
        onSuccess: () => {
            onClose();
        },
        onError: (e) => {
            if (e instanceof UnauthenticatedError || e instanceof UnauthorizedError) {
                useSnackbar.add("You do not have permission to remove this unit.", {
                    variant: "error",
                });
            } else {
                useSnackbar.add("Could not remove unit. Please try again later.", {
                    variant: "error",
                });
            }
        },
    });

    useEffect(() => {
        if (open) {
            setDate(defaultDate ?? null);
            setSupportingDocuments([]);
        }
    }, [open, defaultDate]);

    return (
        <Dialog
            open={open}
            onClose={() => {
                if (!remove.isPending) {
                    onClose();
                }
            }}
        >
            <div className="space-y-6 p-8">
                <p className="text-lg font-medium">Remove unit from all coverages?</p>
                <div className="flex flex-col items-center">
                    <p className="text-stone-500">Date of removal</p>
                    <input
                        type="date"
                        value={date || ""}
                        onChange={(e) => setDate(e.target.value || null)}
                        className="rounded border border-stone-300 p-2"
                    />
                </div>
                <div className="flex items-end justify-end gap-8">
                    <button className="p-2 text-stone-600 hover:bg-stone-50" disabled={remove.isPending} onClick={() => onClose()}>
                        Cancel
                    </button>
                    <PrimaryButton
                        disabled={remove.isPending}
                        onClick={() => {
                            const change = {
                                id: unitId,
                                coverages: coverages?.map((c) => ({
                                    ...c,
                                    remove: true,
                                })),
                                atDate: date || undefined,
                            };
                            const label = `Remove ${unitType.slice(0, -1)}`;
                            if (deferPolicyChanges) {
                                const { id, ...changes } = change;
                                pendingPolicyChanges.addChange({
                                    accountId,
                                    kind: "equipment",
                                    label,
                                    detail: pendingChangeDetail,
                                    submit: () => api.equipment.update(unitType, accountId, id, changes),
                                    unitUpdate: { kind: "equipment", type: unitType, id, changes, originalValues: { coverages } },
                                    supportingDocuments: supportingDocuments.length ? supportingDocuments : undefined,
                                });
                                onClose();
                            } else {
                                remove.mutate(change, {
                                    onSuccess: () => {
                                        if (supportingDocuments.length) {
                                            uploadSupportingDocuments(
                                                accountId,
                                                supportingDocuments,
                                                `${label}${pendingChangeDetail ? ` ${pendingChangeDetail}` : ""}`
                                            );
                                        }
                                    },
                                });
                            }
                        }}
                    >
                        {remove.isPending && <CircularProgress size="1rem" classes={{ svg: "text-white" }} />}
                        Remove
                    </PrimaryButton>
                </div>
                <div className="space-y-2 border-t border-stone-200 pt-6">
                    <p className="font-medium text-stone-800">Add supporting documents (optional)</p>
                    <SupportingDocumentsInput
                        id={`remove-unit-documents-${unitType}-${unitId}`}
                        value={supportingDocuments}
                        onChange={setSupportingDocuments}
                        hint="Bill of Sale, Lease termination document, etc."
                    />
                </div>
            </div>
        </Dialog>
    );
}
